import moment from 'moment';
import { CloudLineStatus, ICloudLine, ICloudRecordSummary } from '../shared-services/cloud-emissions/cloud-emissions.model';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const CLOUD_STATUS_CHIPS: Record<CloudLineStatus, { label: string, color: ChipColor }> = {
  READY: { label: 'Ready to book', color: 'primary' },
  REVIEW: { label: 'Needs a decision', color: 'warning' },
  NEEDS_SETUP: { label: 'Waiting for setup', color: 'default' },
  COMMITTED: { label: 'Booked', color: 'success' },
  RESTATED: { label: 'Restated by the vendor', color: 'warning' },
  LOCKED: { label: 'Period locked', color: 'default' },
  SKIPPED: { label: 'Skipped', color: 'default' },
};

/** 'YYYY-MM' to 'March 2026'. */
export const formatMonthKey = (monthKey?: string | null): string =>
  monthKey ? moment(`${monthKey}-01`, 'YYYY-MM-DD').format('MMMM YYYY') : '';

export const formatRecordDates = (record: ICloudRecordSummary): string => {
  const start = moment(record.startDate).utc().format('D MMM YYYY');
  const end = moment(record.endDate).utc().format('D MMM YYYY');
  return start === end ? start : `${start} to ${end}`;
};

export const formatInvoiceAmount = (record: ICloudRecordSummary): string | null => {
  if (!record.invoice) return null;
  const amount = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(record.invoice.amount);
  return record.invoice.currencyCode ? `${record.invoice.currencyCode} ${amount}` : amount;
};

/**
 * One sentence on what booking this month will do, or why it is waiting. Consequence first,
 * so nobody books a month without knowing what it replaces.
 */
export const describeDecision = (
  line: ICloudLine,
  records: Record<string, ICloudRecordSummary>,
  vendorName: string,
): string => {
  const match = line.carbonDetail.match;
  if (line.carbonStatus === 'COMMITTED') {
    return match?.action === 'ATTACH'
      ? 'Booked on the invoice record in place of its spend estimate.'
      : 'Booked as its own record for the month.';
  }
  if (line.carbonStatus === 'RESTATED') {
    return 'The vendor changed this figure after it was booked. Booking again updates the record.';
  }
  if (line.carbonStatus === 'SKIPPED') return 'You chose not to use this month.';
  if (line.carbonStatus === 'LOCKED') return 'The report period this month belongs to is locked, so it was not booked.';
  if (!match) return '';
  switch (match.reason || match.action) {
    case 'SUPPLIER_MAPPING':
      return 'Confirm which suppliers your cloud invoices arrive under before anything is booked.';
    case 'DEFAULT_CATEGORY':
      return 'Choose a default category before anything is booked.';
    case 'SEVERAL_CANDIDATES':
      return 'More than one invoice record could carry this month. Choose the one it belongs to.';
    case 'BUNDLED_SUPPLIER':
      return 'This supplier also bills services the vendor report does not cover, so its invoice estimate is not replaced automatically.';
    case 'SUSPECTED_UNMAPPED_INVOICE':
      return `A spend record from a supplier that looks like ${vendorName} falls in this month, but that supplier is not in your mapping. Check it first, or this month may be counted twice.`;
    case 'MANUAL_MATCHING':
      return 'Invoices are matched by hand for this connection. Choose where this month belongs.';
    case 'ATTACH': {
      const record = match.emissionId ? records[match.emissionId] : undefined;
      return record
        ? `Will replace the spend estimate on the invoice record dated ${formatRecordDates(record)}.`
        : 'Will replace the spend estimate on the paired invoice record.';
    }
    case 'STANDALONE':
      return 'No invoice record was found for this month, so it will be booked as its own record.';
    default:
      return '';
  }
};
